import React, { useEffect, useState, useCallback } from "react";
import { apiGET, apiPOST, apiPUT } from "../utilities/apiHelpers";
import { API_URL } from "../config";
import scrollToTop from "../utilities/scrollToTop";
import ButtonWithLoader from "../components/Button/ButtonWithLoader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import EnquiryCard from "../components/productComponents/EnquiryCard";
import EnquiryPaymentSummary from "../components/PaymentDetails/EnquirayPaymentSummary";
import EnquirayPrescription from "../components/presecription/EnquirayPrescription";
import { toast } from "react-toastify";


const EnquiryOrderSummary = () => {
    const { id: orderId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [enquirySummaryDetails, setEnquirySummaryDetails] = useState([]);
    const [selectedOption, setSelectedOption] = useState('online');
    const [globalConfig, setGlobalConfig] = useState([]);

    // Get data passed from navigation state
    const passedEnquiryData = location.state?.enquiryData;
    const passedGlobalConfig = location.state?.globalConfig;

    console.log("Order ID from params:", orderId);
    console.log("Passed data from navigation:", passedEnquiryData);
    console.log("enquirySummaryDetails::>", enquirySummaryDetails);

    // ─────────────────────────────────────────────
    // Load Razorpay script dynamically
    // ─────────────────────────────────────────────
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = process.env.REACT_APP_RAZORPAY_URL || "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    // ─────────────────────────────────────────────
    // Open Razorpay Modal (Configured for UPI, Netbanking & Wallets)
    // ─────────────────────────────────────────────
    const openRazorpayModal = (checkoutData, currentOrderId) => {
        const options = {
            key: checkoutData.key_id,
            amount: checkoutData.amount,
            currency: checkoutData.currency,
            order_id: checkoutData.razorpayOrderId,
            name: "MEDiiAPP",
            description: "Order Payment",

            // ─────────────────────────────────────────────
            // CONFIG TO REMOVE CARDS & SHOW DIGITAL ONLY
            // ─────────────────────────────────────────────
            config: {
                display: {
                    blocks: {
                        digital_payments: {
                            name: "Select Payment Method",
                            instruments: [
                                { method: "upi" },
                                { method: "netbanking" },
                                { method: "wallet" }
                            ]
                        }
                    },
                    sequence: ["block.digital_payments"],
                    preferences: {
                        show_default_blocks: false // Hides the restricted Card block
                    }
                }
            },
            // ─────────────────────────────────────────────

            prefill: {
                name: checkoutData.customer_name,
                email: checkoutData.customer_email,
                contact: checkoutData.customer_phone || "",
            },
            theme: {
                color: "#14967F", // Your brand color
            },

            // ─────────────────────────────────────────────
            // Called when payment is successful
            // ─────────────────────────────────────────────
            handler: async function (response) {
                try {
                    setLoading(true);
                    const verifyPayload = {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        orderId: currentOrderId
                    };

                    const verifyResponse = await apiPOST(`/v1/payment/verify-payment`, verifyPayload);

                    if (verifyResponse.status) {
                        toast.success("Payment successful!");
                        setLoading(false);
                        navigate(`/success?orderId=${currentOrderId}`, {
                            state: { paymentData: verifyResponse?.data?.data }
                        });
                    } else {
                        toast.error("Payment verification failed");
                        setLoading(false);
                        navigate(`/cancel?orderId=${currentOrderId}`);
                    }
                } catch (error) {
                    console.error("Error verifying payment::", error);
                    toast.error("Error verifying payment");
                    setLoading(false);
                }
            },

            // Called when user closes the modal
            modal: {
                ondismiss: function () {
                    toast.warning("Payment cancelled");
                    setLoading(false);
                },
            },
        };

        const rzp = new window.Razorpay(options);

        // Handle payment failures
        rzp.on("payment.failed", function (response) {
            console.error("Payment failed::", response.error);
            toast.error(`Payment failed: ${response.error.description}`);
            setLoading(false);
        });

        rzp.open();
    };

    const getEnquiryData = useCallback(async () => {
        if (!orderId) {
            toast.error('Order ID is missing');
            navigate('/');
            return;
        }

        // ─────────────────────────────────────────────
        // If data was passed via navigation, use it instead of fetching
        // ─────────────────────────────────────────────
        if (passedEnquiryData) {
            console.log("Using passed data from navigation - no API call needed");
            setEnquirySummaryDetails([passedEnquiryData]);
            return;
        }

        // ─────────────────────────────────────────────
        // Otherwise, fetch data from API (for direct URL access)
        // ─────────────────────────────────────────────
        console.log("No passed data - fetching from API");
        try {
            const response = await apiGET(`${API_URL}/v1/order/${orderId}`);

            if (response?.data?.status) {
                setEnquirySummaryDetails(response?.data?.data);
            } else {
                toast.error(response?.data?.message || 'Failed to fetch order details');
            }
        } catch (error) {
            console.error("Error fetching order data:", error);
            toast.error(error?.response?.data?.message || 'Invalid order ID or order not found');
        }
    }, [orderId, passedEnquiryData, navigate]);

    const fetchGlobalConfig = useCallback(async () => {
        if (passedGlobalConfig) {
            console.log("Using passed global config from navigation");
            setGlobalConfig(passedGlobalConfig);
            return;
        }

        try {
            const response = await apiGET(`/v1/global-config/get-config`);
            if (response.status) {
                const configArray = response?.data?.data?.data;

                // Find the latest config by updatedAt timestamp
                const latestConfig = configArray.reduce((latest, current) => {
                    return new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest;
                });

                console.log("Selected latest config for location:", latestConfig.location);
                setGlobalConfig(latestConfig);
            } else {
                toast.error('Error fetching global config');
            }
        } catch (error) {
            console.error('Error fetching global config:', error);
            toast.error('Error loading configuration');
        }
    }, [passedGlobalConfig]);

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleCheckout = async () => {
        if (!orderId) {
            toast.error('Order ID is missing');
            return;
        }

        setLoading(true);

        // ─────────────────────────────────────────────
        // COD Flow
        // ─────────────────────────────────────────────
        if (selectedOption === 'cod') {
            const updateCodOrderPayload = {
                orderType: selectedOption,
                mode: "order",
                delivery_charges: globalConfig?.deliveryCharges || 0,
                packaging_charges: globalConfig?.packagingCharges || 0,
            };
            console.log("Updating order for COD with payload:", updateCodOrderPayload);
            try {
                const updateCodOrderResponse = await apiPUT(`/v1/order/update/${orderId}`, updateCodOrderPayload);
                if (updateCodOrderResponse?.data?.status) {
                    toast.success('Order Placed successfully');
                    setLoading(false);
                    navigate(`/cod-success-page/${updateCodOrderResponse?.data?.data?.id}`);
                } else {
                    toast.error(updateCodOrderResponse?.data?.message || 'Something went wrong');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error adding COD order::', error);
                toast.error(error?.response?.data?.message || 'Failed to place COD order');
                setLoading(false);
            }

            // ─────────────────────────────────────────────
            // ONLINE PAYMENT Flow (Using Razorpay Modal)
            // ─────────────────────────────────────────────
        } else {
            try {
                // Step 1: Load Razorpay script
                const scriptLoaded = await loadRazorpayScript();
                if (!scriptLoaded) {
                    toast.error("Failed to load Razorpay. Check your connection.");
                    setLoading(false);
                    return;
                }

                // Step 2: Update order type
                const updateOrderPayload = {
                    orderType: selectedOption,
                    delivery_charges: globalConfig?.delivery_charges || 0,
                    packaging_charges: globalConfig?.packaging_charges || 0,
                };

                const updateOrderResponse = await apiPUT(`/v1/order/update/${orderId}`, updateOrderPayload);
                if (!updateOrderResponse?.data?.status) {
                    toast.error(updateOrderResponse?.data?.message || "Error updating order");
                    setLoading(false);
                    return;
                }

                // Step 3: Create Razorpay checkout session
                const checkoutPayload = {
                    mode: 'order',
                    orderType: selectedOption,
                };

                const checkoutResponse = await apiPOST(
                    `${API_URL}/v1/payment/create-checkout/${orderId}`,
                    checkoutPayload
                );

                if (checkoutResponse?.status) {
                    // Step 4: Open Razorpay modal
                    // Previously: window.location.replace(checkoutUrl)
                    // Now: open Razorpay modal directly on page
                    openRazorpayModal(
                        checkoutResponse?.data?.data,
                        orderId
                    );
                } else {
                    toast.error(checkoutResponse?.data?.message || 'Failed to create checkout session');
                    setLoading(false);
                }

            } catch (error) {
                console.error("Error processing payment::", error);
                toast.error(error?.response?.data?.message || 'Error processing payment');
                setLoading(false);
            }
        }
    };


    useEffect(() => {
        getEnquiryData();
        fetchGlobalConfig();
    }, [getEnquiryData, fetchGlobalConfig]);

    useEffect(() => {
        scrollToTop();
    }, []);

    return (
        <div className="container mx-auto p-4 bg-white">
            {/* Back Button */}
            <div className="mb-4">
                <button
                    onClick={() => navigate('/enquiries')}
                    className="flex items-center gap-2 text-[#14967F] hover:text-[#0f7a66] transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Enquiries
                </button>
            </div>

            <div className="lg:flex gap-5">
                <div className="w-full">
                    {enquirySummaryDetails?.length ? (
                        <EnquiryCard
                            cardItem={enquirySummaryDetails[0]?.orderItemData}
                            globalConfig={globalConfig}
                        />
                    ) : (
                        <div className="text-center py-8">
                            <div className="animate-pulse">Loading enquiry details...</div>
                        </div>
                    )}

                    <div className="">
                        <h2 className="text-2xl font-bold text-[18px]">Select Your Payment Method</h2>
                        <div className="space-y-4 p-4">
                            <div className={`flex items-center p-4 border rounded-lg bg-[#FFFFFF] ${selectedOption === 'online' ? 'border-green-500' : 'border-gray-300'}`}>
                                <input
                                    type="radio"
                                    id="payOnline"
                                    name="paymentOption"
                                    value="online"
                                    checked={selectedOption === 'online'}
                                    onChange={handleOptionChange}
                                    className="form-radio h-5 w-5 text-green-500 cursor-pointer"
                                />
                                <label htmlFor="payOnline" className="ml-3 text-lg cursor-pointer font-medium">
                                    Pay Online
                                </label>
                            </div>
                            <div className={`flex items-center p-4 border rounded-lg bg-[#FFFFFF] ${selectedOption === 'cod' ? 'border-green-500' : 'border-gray-300'}`}>
                                <input
                                    type="radio"
                                    id="cashOnDelivery"
                                    name="paymentOption"
                                    value="cod"
                                    checked={selectedOption === 'cod'}
                                    onChange={handleOptionChange}
                                    className="form-radio h-5 w-5 text-green-500 cursor-pointer"
                                />
                                <label htmlFor="cashOnDelivery" className="ml-3 text-lg cursor-pointer font-medium">
                                    Cash on Delivery
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end w-full">
                        <ButtonWithLoader
                            loading={loading}
                            buttonText={`${selectedOption === 'cod' ? "Place order" : "Proceed to payment"}`}
                            width={"w-[200px]"}
                            onClick={handleCheckout}
                        />
                    </div>
                </div>

                <div className="flex flex-col w-full">
                    <EnquiryPaymentSummary
                        enquirySummaryDetails={enquirySummaryDetails}
                        globalConfig={globalConfig}
                    />
                    <EnquirayPrescription prescription={enquirySummaryDetails} />
                </div>
            </div>
        </div>
    );
};

export default EnquiryOrderSummary;
